/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useContext } from 'react';

import { ContainerLayout } from 'components/layouts/ContainerLayout';
import { useTranslation } from 'react-i18next';
import {
  approveApprovalRequestResult,
  cancelApprovalRequestResult,
  findApproveRequestDetail,
  rejectApprovalRequestResult,
  rewriteApprovalRequestResult,
} from 'apis/approves/approves';
import { useLoading } from 'components/process/Loading';
import ApproveDetailLineInfo from './ApproveDetailLineInfo';
import ApproveDetailBasicInfo from './ApproveDetailBasicInfo';
import {
  ApprovalAppd,
  ApproveRequestDetail,
  clearApprovalAppd,
  clearApproveRequestDetail,
} from 'models/approves/ReferenceOpinion';
import useSessionStore from 'stores/useSessionStore';
import ApproveModal from 'components/modals/approves/ApproveModal';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import ApproveRequestDetailCase, { ApproveRequestPageType } from './ApproveRequestDetailCase';
import { GlobalBtnGroup } from 'components/layouts/ContentLayout';
import { Button } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import { MenuContext } from 'App';
import { MenuContextType } from 'models/admin/Menu';
import { ManagementMode } from 'models/common/Common';
import SendEmailModal from 'components/modals/common/SendEmailModal';
import ApproveSendEmailModal from 'components/modals/common/ApproveSendEmailModal';
import { EVENT_TYPES, EventBus } from '../../utils/EventUtil';

interface LocationState {
  upprMnuUrl?: string;
}

/**
 * @desc 결재 페이지
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.10.06   sungim.kim2   [고도화] 병렬 결재 추가
 * 2026.01.20   jihwan89      CSR E20260108-002100 결재문서 임시저장(TMP)/상신취소(CCL)가 아닌 상태의 결재 공유메일 추가
 */

const ApproveRequestDetailPage = () => {
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { userId } = useSessionStore();
  const [approveInfo, setApproveInfo] = useState<ApproveRequestDetail>(clearApproveRequestDetail);
  const [waitApprovalAppd, setWaitApprovalAppd] = useState<ApprovalAppd>(clearApprovalAppd);
  const [isApprover, setIsApprover] = useState<boolean>(false);
  const [approveModalOpen, setApproveModalOpen] = useState<boolean | 'APPROVE' | 'REJECT'>(false);
  const navigate = useNavigate();
  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();
  const menuContext = useContext<MenuContextType>(MenuContext);
  const locationState: LocationState = useLocation().state;
  const [isLocationState, setLocationState] = useState<LocationState>({
    upprMnuUrl: locationState?.upprMnuUrl,
  });
  const [paramDatail, setParamDetail] = useState<any>({
    pageSeqId: '',
    pageId: '',
    aprReqId: '',
    mode: '',
  });

  const [searchParams] = useSearchParams();
  const [searchCondition, setSearchCondition] = useState({
    aprReqId: searchParams?.get('aprReqId') || '',
    appintUrl: searchParams?.get('appint_url') || '',
    rtnUrl: searchParams?.get('rtnUrl') || '',
    isShowMenu: searchParams?.get('showMenu') !== 'N',
  });

  const [isOpenEmailModal, setOpenEmailModal] = useState<boolean>(false);
  const [getConditionSendMail, setConditionSendMail] = useState<any>();

  const REWRITE_ALLOWED_PAGE_IDS = [
    //재작성 버튼 표시할 페이지들
    ApproveRequestPageType.SETUP_SCHD_REG,
    ApproveRequestPageType.SETUP_SCHD_CHG,
    ApproveRequestPageType.EXCP_AUTH_REQ,
    ApproveRequestPageType.ELM_AUTH_REQ,
  ];

  const DELETE_ALLOWED_PAGE_IDS = [
    //삭제 버튼 표시할 페이지들
    ApproveRequestPageType.SETUP_SCHD_REG,
    ApproveRequestPageType.SETUP_SCHD_CHG,
    ApproveRequestPageType.EXCP_AUTH_REQ,
    ApproveRequestPageType.ELM_AUTH_REQ,
  ];

  const init = async () => {
    if (!searchCondition.aprReqId) {
      openMessageBar({
        type: 'error',
        content: t('com.label.잘못된 접근입니다.', '잘못된 접근입니다.'),
      });
      return;
    }

    openLoading(true);
    const response = await findApproveRequestDetail(searchCondition.aprReqId);
    openLoading(false);
    // 임시저장 상태인 경우 작성화면으로 전환
    if (response?.aprReqProgStatCd === 'TMP') {
      EventBus.publish(EVENT_TYPES.ELM_AUTH_REQ_LIST);
      menuContext.closeCurrentTab(menuContext);
      // 임시저장인 경우 작성자만 접근 가능
      if (response?.aprReqUserId === userId) {
        setTimeout(() => {
          navigate('/approves/approve-request-page?aprReqId=' + response.aprReqId, {
            state: {
              upprMnuUrl: '/system/auth-request-list',
              mnuId: '/system/auth-request-list',
              mnuNm: t('asst.label.결재현황', '결재현황'),
            },
          });
        }, 100);
      } else {
        openMessageBar({
          type: 'warning',
          content:
            `${response?.aprReqUserNm}` +
            String(t('com.msg.님이 작성중입니다.', '님이 작성중입니다.')),
        });
      }
      return;
    }
    setApproveInfo(response);
    const nowApprove = response.approvalAppd?.find((item) => item.aprPsgStatCd == 'WAIT');

    if (nowApprove && nowApprove.aprAprvUserId === userId) {
      setWaitApprovalAppd(nowApprove);
      setIsApprover(true);
    } else {
      // [고도화] 병렬결재 처리
      // 병렬은 snb 번호가 동일함  병렬결재 대상인 경우 승인 활성화 하도록 변경.
      if (nowApprove && nowApprove.prlYn === 'Y') {
        const nextApprove = response.approvalAppd?.find(
          (o) =>
            nowApprove.aprLnSnb == o.aprLnSnb &&
            o.aprAprvUserId == userId &&
            o.aprPsgStatCd == 'WAIT'
        );
        if (nextApprove) {
          setWaitApprovalAppd(nextApprove);
          setIsApprover(true);
        }
      }
    }
  };

  useEffect(() => {
    if (searchParams?.get('aprReqId')) {
      setSearchCondition({
        aprReqId: searchParams?.get('aprReqId') || '',
        appintUrl: searchParams?.get('appint_url') || '',
        rtnUrl: searchParams?.get('rtnUrl') || '',
        isShowMenu: searchParams?.get('showMenu') === 'N' ? false : true,
      });
    }
  }, [searchParams]);

  useEffect(() => {
    init();
  }, [searchCondition.aprReqId]);

  const navigateToListPage = () => {
    EventBus.publish(EVENT_TYPES.ELM_AUTH_REQ_LIST);
    if (isLocationState.upprMnuUrl) menuContext.closeCurrentTab(menuContext);
    navigate(searchCondition.rtnUrl || '/approves/approve-management-page');
  };

  const handleApprove = async (opinion: string) => {
    openLoading(true);
    const response = await approveApprovalRequestResult({
      ...approveInfo,
      resultApprovalOpinion: opinion,
      resultAprLnSeq: waitApprovalAppd?.aprLnSeq ?? '',
      resultAprLnSnb: waitApprovalAppd?.aprLnSnb ?? '',
    });
    openLoading(false);

    if (response.successOrNot === 'Y') {
      openMessageBar({
        type: 'confirm',
        content: t('com.label.승인되었습니다.', '승인되었습니다.'),
      });
      //전자결재 화면에서 접근했을 경우 appint_url 값으로 이동
      if (searchCondition.appintUrl) {
        window.location.href = searchCondition.appintUrl;
      } else {
        navigateToListPage();
      }
    } else {
      openMessageBar({
        type: 'error',
        content:
          typeof response.data === 'string'
            ? response.data
            : t('com.label.요청에 실패했습니다.', '요청에 실패했습니다.'),
      });
    }
  };

  const handleRejectApprove = async (opinion: string) => {
    openLoading(true);
    const response = await rejectApprovalRequestResult({
      ...approveInfo,
      resultApprovalOpinion: opinion,
      resultAprLnSeq: waitApprovalAppd?.aprLnSeq ?? '', //  일단 seq로 변경
      resultAprLnSnb: waitApprovalAppd?.aprLnSnb ?? '', // [고도화] 병렬결재를 위해 snb 전송
    });

    openLoading(false);
    if (response.successOrNot === 'Y') {
      openMessageBar({
        type: 'confirm',
        content: t('com.msg.반려 완료되었습니다.', '반려 완료되었습니다.'),
      });
      // 전자결재 화면에서 접근했을 경우 appint_url 값으로 이동
      if (searchCondition.appintUrl) {
        window.location.href = searchCondition.appintUrl;
      } else {
        navigateToListPage();
      }
    } else {
      openMessageBar({
        type: 'error',
        content:
          typeof response.data === 'string'
            ? response.data
            : t('com.label.요청에 실패했습니다.', '요청에 실패했습니다.'),
      });
    }
  };

  const handleCancelApprove = () => {
    openCommonModal({
      modalType: 'confirm',
      content: t(`com.msg.결재취소하시겠습니까?`, `결재취소하시겠습니까?`),
      yesCallback: async () => {
        openLoading(true);
        const response = await cancelApprovalRequestResult(approveInfo.aprReqId);
        openLoading(false);

        if (response.successOrNot === 'Y') {
          openMessageBar({
            type: 'confirm',
            content: t('com.msg.결재취소되었습니다.', '결재취소되었습니다.'),
          });
          navigateToListPage();
        } else {
          openMessageBar({
            type: 'error',
            content:
              typeof response.data === 'string'
                ? response.data
                : t('com.msg.오류가 발생하였습니다.', '오류가 발생하였습니다.'),
          });
        }
      },
    });
  };

  // [고도화] 메일 공유 버튼
  const handleSendMailButton = () => {
    setConditionSendMail({
      aprReqId: searchCondition?.aprReqId || '',
      aprReqTitNm: approveInfo?.aprReqTitNm || '',
    });

    setOpenEmailModal(true);
  };

  /**
   * 반려 상태인 결재 재작성 처리
   * [26.03.18] 반려인 경우 재작성 노출 > 클릭 시 결재정보 복사해서 신규 결재데이터 생성 (ELM권한요청 적용)
   */
  const handleRewrite = async () => {
    openCommonModal({
      modalType: 'confirm',
      content: t(`com.msg.재작성하시겠습니까?`, `재작성하시겠습니까?`),
      yesCallback: async () => {
        openLoading(true);
        const response = await rewriteApprovalRequestResult(approveInfo?.aprReqId);
        openLoading(false);

        if (response.successOrNot === 'Y') {
          openMessageBar({
            type: 'confirm',
            content: t('com.msg.생성되었습니다.', '생성되었습니다.'),
          });
          EventBus.publish(EVENT_TYPES.ELM_AUTH_REQ_LIST);
          menuContext.closeCurrentTab(menuContext);
          if (response.data) {
            setTimeout(() => {
              navigate('/approves/approve-request-page?aprReqId=' + response.data, {
                replace: true,
                state: {
                  upprMnuUrl:
                    approveInfo?.optValCtn1 == ApproveRequestPageType.ELM_AUTH_REQ
                      ? '/system/auth-request-list'
                      : '/approves/approve-management-page',
                  mnuId: '/system/auth-request-page',
                  mnuNm: t('com.label.결재현황', '결재현황'),
                },
              });
            }, 100);
          }
        } else {
          openMessageBar({
            type: 'error',
            content:
              typeof response.data === 'string'
                ? response.data
                : t('com.msg.오류가 발생하였습니다.', '오류가 발생하였습니다.'),
          });
        }
      },
    });
  };

  const drawButtons = () => {
    return (
      <>
        {!['TMP', 'CCL', ''].includes(approveInfo.aprReqProgStatCd) && (
          <Button
            css={IconButton.button}
            className="mail"
            onClick={handleSendMailButton}
            disableRipple
          >
            {t('com.button.메일 공유', '메일 공유')}
          </Button>
        )}
        {!searchCondition.appintUrl && searchCondition.isShowMenu && (
          <Button
            css={IconButton.button}
            className="list"
            disableRipple
            onClick={navigateToListPage}
          >
            {t('com.button.목록', '목록')}
          </Button>
        )}

        {['REQ'].includes(approveInfo.aprReqProgStatCd) && approveInfo.aprReqUserId === userId && (
          <Button
            css={IconButton.button}
            className="cancel"
            disableRipple
            onClick={handleCancelApprove}
          >
            {t('com.button.상신취소', '결재취소')}
          </Button>
        )}

        {['REQ', 'INP'].includes(approveInfo.aprReqProgStatCd) && isApprover && (
          <>
            <Button
              css={IconButton.button}
              className="cancel"
              onClick={() => {
                setApproveModalOpen('REJECT');
              }}
              disableRipple
            >
              {t('com.button.반려', '반려')}
            </Button>

            <Button
              css={IconButton.button}
              className="ok"
              onClick={() => {
                setApproveModalOpen('APPROVE');
              }}
              disableRipple
            >
              {t('com.button.승인', '승인')}
            </Button>
          </>
        )}
        {approveInfo.optValCtn1 &&
          REWRITE_ALLOWED_PAGE_IDS.includes(approveInfo.optValCtn1 as ApproveRequestPageType) &&
          'REJ' === approveInfo?.aprReqProgStatCd &&
          approveInfo?.aprReqUserId === userId && (
            <Button css={IconButton.button} className="write" onClick={handleRewrite}>
              {t('com.button.재작성', '재작성')}
            </Button>
          )}
      </>
    );
  };
  return (
    <>
      <ContainerLayout>
        <GlobalBtnGroup className="top">{drawButtons()}</GlobalBtnGroup>

        <ApproveDetailBasicInfo approveInfo={approveInfo} />

        {approveInfo?.optValCtn1 && (
          <ApproveRequestDetailCase
            pageId={approveInfo.optValCtn1}
            condition={approveInfo.condition}
            isReadOnly={true}
            aprReqId={searchCondition.aprReqId}
          />
        )}

        <ApproveDetailLineInfo
          pageId={approveInfo.optValCtn1}
          approvalAppd={approveInfo.approvalAppd || []}
          approvalInfr={approveInfo.approvalInfr || []}
        />

        <GlobalBtnGroup>{drawButtons()}</GlobalBtnGroup>
      </ContainerLayout>

      {approveModalOpen && (
        <ApproveModal
          open={approveModalOpen}
          title={t('com.label.결재의견', '결재의견')}
          approvalType={'0'}
          close={() => setApproveModalOpen(false)}
          approveCallback={(msg) => {
            setApproveModalOpen(false);
            handleApprove(msg);
          }}
          rejectCallback={(msg) => {
            setApproveModalOpen(false);
            handleRejectApprove(msg);
          }}
        />
      )}
      {isOpenEmailModal && (
        <ApproveSendEmailModal
          open={isOpenEmailModal}
          close={() => setOpenEmailModal(false)}
          condition={getConditionSendMail}
        />
      )}
    </>
  );
};

export default ApproveRequestDetailPage;
